/**
 * Cookie consent banner (UK ICO / GDPR compliant).
 *
 * What it does:
 *   • Shows on first visit, bottom-fixed, dismissable.
 *   • Two clear choices: "Accept all" and "Essential only".
 *   • Choice is stored in localStorage so it never reappears for that
 *     browser. (Per ICO guidance, consent must be revisitable — we
 *     expose that via the small "Cookie settings" link in the footer.)
 *   • Does not auto-set any non-essential cookies until "Accept all"
 *     is chosen (currently Sentry browser-tracing is the only one
 *     that could be argued non-essential — we still capture errors
 *     either way because that falls under legitimate interest).
 *
 * Storage key: 'fl_cookie_consent' → 'accepted' | 'essential' | null.
 */
import React, { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';

import { Button } from './ui/button';

const STORAGE_KEY = 'fl_cookie_consent';

export const getCookieConsent = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'accepted' || v === 'essential' ? v : null;
  } catch {
    return null;
  }
};

const setCookieConsent = (value) => {
  try { window.localStorage.setItem(STORAGE_KEY, value); } catch { /* private mode */ }
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  // Defer the check to mount so SSR/hydration mismatch is impossible
  // (CRA is fully client-side but the pattern keeps behaviour consistent).
  useEffect(() => {
    if (getCookieConsent() === null) setVisible(true);
    // Listen for "open cookie settings" requests from the footer.
    const onOpen = () => setVisible(true);
    window.addEventListener('forge:open-cookie-settings', onOpen);
    return () => window.removeEventListener('forge:open-cookie-settings', onOpen);
  }, []);

  if (!visible) return null;

  const choose = (value) => {
    setCookieConsent(value);
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#E2E8F0] shadow-[0_-4px_24px_-12px_rgba(15,23,42,0.15)]"
      role="dialog"
      aria-label="Cookie preferences"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-5xl mx-auto p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        <div className="flex items-start gap-3 flex-1">
          <Cookie className="w-5 h-5 text-[#0052FF] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#475569] leading-relaxed">
            We use a small number of cookies to keep you logged in and process payments securely.
            Optional cookies help us spot issues faster.{' '}
            <a
              href="/about#about-section-security"
              className="text-[#0052FF] hover:underline font-medium"
              data-testid="cookie-consent-policy-link"
            >
              Read our cookie policy
            </a>
            .
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => choose('essential')}
            className="text-sm"
            data-testid="cookie-consent-essential"
          >
            Essential only
          </Button>
          <Button
            type="button"
            onClick={() => choose('accepted')}
            className="bg-[#0052FF] text-white hover:bg-[#003ECC] text-sm"
            data-testid="cookie-consent-accept"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
